import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ReceiptText, Search as SearchIcon, Building2, ChevronRight, Wallet, TrendingUp, AlertOctagon } from "lucide-react";
import { api } from "@/lib/api";
import { KPI, SectionCard } from "./AdminJobCards";

const inr = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v || 0));
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

const PAY_CFG = {
  paid: { cls: "bg-[#12B76A]/10 text-[#027A48] border-[#12B76A]/25", label: "Paid" },
  partially_paid: { cls: "bg-[#F79009]/15 text-[#B54708] border-[#F79009]/30", label: "Partial" },
  unpaid: { cls: "bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25", label: "Unpaid" },
  refunded: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "Refunded" },
};
const PayChip = ({ s }) => {
  const c = PAY_CFG[s] || { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: s || "—" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${c.cls}`}>{c.label}</span>;
};

export default function AdminInvoices() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const [s, l] = await Promise.all([
      api.get("/admin/ops/invoices/summary").then((r) => r.data).catch(() => null),
      api.get("/admin/ops/invoices", { params: { q: q || undefined, payment_status: paymentStatus || undefined, limit: 150 } }).then((r) => r.data).catch(() => ({ rows: [] })),
    ]);
    setSummary(s);
    setRows(l.rows || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, [paymentStatus]);

  const kpis = summary?.kpis || {};
  const byPay = summary?.by_payment_status || {};
  const byType = summary?.by_invoice_type || {};
  const top = summary?.top_garages || [];

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Platform Ops</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Invoices</h1>
        <p className="text-[13px] text-[#667085] mt-0.5">Platform-wide billing rollup — billed vs paid, GST split, and top-earning garages.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI testId="inv-kpi-total" label="Total Invoices" value={kpis.total ?? "—"} Icon={ReceiptText} sub={`+${kpis.issued_this_week ?? 0} this week`} />
        <KPI testId="inv-kpi-billed" label="Billed" value={inr(kpis.billed ?? 0)} Icon={Wallet} tone="blue" />
        <KPI testId="inv-kpi-paid" label="Collected" value={inr(kpis.paid ?? 0)} Icon={TrendingUp} tone="green" />
        <KPI testId="inv-kpi-outstanding" label="Outstanding" value={inr(kpis.outstanding ?? 0)} Icon={AlertOctagon} tone="red" />
        <KPI testId="inv-kpi-today" label="Issued Today" value={kpis.issued_today ?? "—"} tone="honeydew" />
        <KPI testId="inv-kpi-week" label="Issued This Week" value={kpis.issued_this_week ?? "—"} tone="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <SectionCard
            title="Invoices"
            subtitle={`${rows.length} row(s)`}
            right={
              <div className="flex items-center gap-2">
                <div className="relative">
                  <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#667085]" />
                  <input
                    data-testid="inv-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
                    placeholder="INV#, customer, vehicle…"
                    className="pl-7 pr-3 h-8 rounded-md border border-[#E4E7EC] bg-white text-[12px] w-56 focus:outline-none focus:border-[#457b9d]"
                  />
                </div>
                <select
                  data-testid="inv-payment-filter"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  className="h-8 px-2 rounded-md border border-[#E4E7EC] bg-white text-[12px] focus:outline-none focus:border-[#457b9d]"
                >
                  <option value="">All payments</option>
                  <option value="paid">Paid</option>
                  <option value="partially_paid">Partial</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="refunded">Refunded</option>
                </select>
                <button onClick={reload} className="h-8 px-3 rounded-md bg-[#1d3557] text-white text-[12px] font-semibold hover:bg-[#152941]">Refresh</button>
              </div>
            }
            testId="inv-table-card"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12px]">
                <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="px-4 py-2 font-semibold">INV #</th>
                    <th className="px-4 py-2 font-semibold">Garage</th>
                    <th className="px-4 py-2 font-semibold">Customer</th>
                    <th className="px-4 py-2 font-semibold">Vehicle</th>
                    <th className="px-4 py-2 font-semibold">Type</th>
                    <th className="px-4 py-2 font-semibold text-right">Billed</th>
                    <th className="px-4 py-2 font-semibold text-right">Outstanding</th>
                    <th className="px-4 py-2 font-semibold">Payment</th>
                    <th className="px-4 py-2 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1FAEE]">
                  {loading && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#667085]">Loading…</td></tr>}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-[#667085]">No invoices match.</td></tr>
                  )}
                  {!loading && rows.map((r) => (
                    <tr key={r.id} data-testid={`inv-row-${r.id}`} onClick={() => window.location.assign(`/admin/invoices/${r.id}`)} className="hover:bg-[#F7F9FC] cursor-pointer">
                      <td className="px-4 py-2 font-mono font-semibold text-[#1d3557]">
                        {r.number || <span className="text-[#667085] italic font-sans font-normal">#{(r.id || "").slice(0, 8)}</span>}
                      </td>
                      <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                        <Link to={`/admin/garages/${r.garage_id}`} className="text-[#457b9d] hover:underline">{r.garage_name}</Link>
                      </td>
                      <td className="px-4 py-2 text-[#1d3557]">{r.customer_name || "—"}</td>
                      <td className="px-4 py-2 font-mono">{r.vehicle_reg || "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold ${r.invoice_type === "gst" ? "bg-[#457b9d]/10 text-[#1d3557] border-[#457b9d]/25" : "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]"}`}>
                          {(r.invoice_type || "").toUpperCase() || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono font-semibold text-[#1d3557] text-right">{inr(r.billed)}</td>
                      <td className={`px-4 py-2 font-mono text-right ${r.outstanding > 0 ? "text-[#e63946] font-semibold" : "text-[#12B76A]"}`}>{inr(r.outstanding)}</td>
                      <td className="px-4 py-2"><PayChip s={r.payment_status} /></td>
                      <td className="px-4 py-2 text-[#667085]">{fmtDate(r.issued_at || r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard title="Payment Split" testId="inv-pay-split">
            <div className="p-5 space-y-2">
              {Object.entries(byPay).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <PayChip s={k} />
                  <div className="flex-1 h-1.5 bg-[#F1FAEE] rounded">
                    <div className="h-full bg-[#457b9d] rounded" style={{ width: `${Math.min(100, (v.count / Math.max(1, kpis.total || 1)) * 100)}%` }} />
                  </div>
                  <div className="text-[12px] font-mono font-bold text-[#1d3557] w-16 text-right">{v.count}</div>
                  <div className="text-[11px] font-mono text-[#667085] w-24 text-right">{inr(v.billed)}</div>
                </div>
              ))}
              {Object.keys(byPay).length === 0 && <div className="text-[13px] text-[#667085] text-center">No data</div>}
            </div>
          </SectionCard>

          <SectionCard title="GST vs Non-GST" testId="inv-gst-split">
            <div className="p-5 space-y-2">
              {Object.entries(byType).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <span className="text-[11px] font-semibold text-[#1d3557] uppercase w-16">{k}</span>
                  <div className="flex-1 h-1.5 bg-[#F1FAEE] rounded">
                    <div className="h-full bg-[#e63946] rounded" style={{ width: `${Math.min(100, (v.count / Math.max(1, kpis.total || 1)) * 100)}%` }} />
                  </div>
                  <div className="text-[12px] font-mono font-bold text-[#1d3557] w-12 text-right">{v.count}</div>
                  <div className="text-[11px] font-mono text-[#667085] w-24 text-right">{inr(v.billed)}</div>
                </div>
              ))}
              {Object.keys(byType).length === 0 && <div className="text-[13px] text-[#667085] text-center">No data</div>}
            </div>
          </SectionCard>

          <SectionCard title="Top Garages by Billed" testId="inv-top-garages">
            <div className="divide-y divide-[#F1FAEE]">
              {top.length === 0 && <div className="p-6 text-center text-[13px] text-[#667085]">No data</div>}
              {top.map((g, i) => (
                <Link key={g.garage_id} to={`/admin/garages/${g.garage_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-[#F7F9FC]">
                  <div className="w-7 h-7 rounded-md grid place-items-center bg-[#F1FAEE] text-[#457b9d]"><Building2 size={14} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#1d3557] truncate">#{i + 1} {g.garage_name}</div>
                    <div className="text-[11px] text-[#667085]">{g.invoices_count} invoice(s)</div>
                  </div>
                  <div className="text-[13px] font-mono font-bold text-[#1d3557]">{inr(g.billed)}</div>
                  <ChevronRight size={13} className="text-[#a8dadc]" />
                </Link>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
